// Sub-components
import {
  fromBase,
  getBaseUnit,
  getQuantity,
  getUnit,
  pickDisplayUnit
} from "./registry";

const DEFAULT_SYSTEM = "metric";
const DEFAULT_LOCALE = "en-US";
const NBSP = "\u00A0";

function isFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}

// normalize backend-style values to FE expectations.
export function normalizeSystem(system) {
  if (!system) return DEFAULT_SYSTEM;
  if (typeof system !== "string") return DEFAULT_SYSTEM;
  const normalized = system.trim().toLowerCase();
  if (normalized === "metric" || normalized === "imperial") {
    return normalized;
  }
  return DEFAULT_SYSTEM;
}
// convert e.g. "de_DE" -> "de-DE"
export function normalizeLocale(locale) {
  if (!locale || typeof locale !== "string") return DEFAULT_LOCALE;
  return locale.trim().replace(/_/g, "-");
}

function resolveFamily(family, issues) {
  try {
    return { familyConfig: getQuantity(family), validFamily: family };
  } catch (error) {
    issues.push({
      code: "UNKNOWN_FAMILY",
      message: `[units/formatMeasurement] Unknown family "${family}". Falling back.`,
      context: { family },
      error
    });
    return null;
  }
}

function resolveDisplayUnit({
  baseValue,
  family,
  system,
  overrideUnit,
  issues
}) {
  const baseUnit = getBaseUnit(family);

  // Explicit override always wins over auto-pick.
  if (overrideUnit) {
    return overrideUnit;
  }

  try {
    return pickDisplayUnit(baseValue, family, system);
  } catch (error) {
    // If band selection fails, stick to the family base unit.
    issues.push({
      code: "AUTO_PICK_FAILED",
      message:
        "[units/formatMeasurement] Failed to auto-pick display unit. Using base unit.",
      context: { family, system, baseUnit, baseValue },
      error
    });
    return baseUnit;
  }
}

function resolveUnitMeta({ family, familyConfig, unit, issues }) {
  try {
    return { unit, meta: getUnit(family, unit) };
  } catch (error) {
    const fallbackUnit = familyConfig.base;
    const fallbackMeta = familyConfig.units[fallbackUnit] || { symbol: "" };

    // Invalid override/auto-picked unit -> fallback to base unit metadata.
    issues.push({
      code: "INVALID_UNIT",
      message: `[units/formatMeasurement] Invalid unit "${unit}" for family "${family}". Using base unit.`,
      context: { family, unit, fallbackUnit },
      error
    });
    return { unit: fallbackUnit, meta: fallbackMeta };
  }
}

function resolveNumericValue({ baseValue, family, unit }) {
  try {
    return { value: fromBase(baseValue, unit, family), issues: [] };
  } catch (error) {
    return {
      value: baseValue,
      issues: [
        {
          code: "CONVERSION_FAILED",
          message: "Failed conversion from base unit.",
          context: { family, unit, baseValue },
          error
        }
      ]
    };
  }
}

function formatNumber(value, locale, decimals) {
  try {
    return {
      value: new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(value),
      issues: []
    };
  } catch (error) {
    return {
      value: new Intl.NumberFormat("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(value),
      issues: [
        {
          code: "INVALID_LOCALE",
          message: `Invalid locale "${locale}", fallback to en-US.`,
          context: { locale, fallbackLocale: "en-US", decimals, value },
          error
        }
      ]
    };
  }
}

/**
 * Formats a base-unit measurement for UI display.
 *
 * Callers must resolve `family` first (typically via
 * {@link resolveUnitFamilyFromPayload} in `utils/units.js`). When family is
 * unknown, use legacy `${value} ${unit}` formatting instead. See `UNITS FORMATTING section in the wiki
 *
 * @param {number} baseValue - Numeric value in the family's base unit.
 * @param {string} family - Quantity family key (e.g. "length", "area").
 * @param {Object} [options]
 * @param {string} [options.system="metric"] - "metric" | "imperial".
 * @param {string} [options.locale="en-US"] - BCP47 locale tag.
 * @param {string} [options.overrideUnit] - Unit id to force output unit.
 * @param {number} [options.precision] - Explicit decimals; overrides family default.
 * @returns {{ value: number, unit: string, symbol: string, formatted: string, issues: Array }}
 */
export function formatMeasurement(baseValue, family, options = {}) {
  const {
    system = DEFAULT_SYSTEM,
    locale = DEFAULT_LOCALE,
    overrideUnit,
    precision
  } = options;
  const issues = [];

  const normalizedSystem = normalizeSystem(system);
  const normalizedLocale = normalizeLocale(locale);

  // Guard against NaN/Infinity/non-number inputs from callers.
  const safeBaseValue = isFiniteNumber(baseValue) ? baseValue : 0;

  // report non-finite input.
  if (!isFiniteNumber(baseValue)) {
    issues.push({
      code: "INVALID_BASE_VALUE",
      message: `[units/formatMeasurement] Non-finite base value received. Falling back to 0.`,
      context: { family, baseValue, fallbackBaseValue: 0 }
    });
  }

  const resolved = resolveFamily(family, issues);

  if (!resolved) {
    const fallbackDecimals =
      Number.isInteger(precision) && precision >= 0 ? precision : 0;
    const fallbackNumber = formatNumber(
      safeBaseValue,
      normalizedLocale,
      fallbackDecimals
    );
    issues.push(...fallbackNumber.issues);
    return {
      value: safeBaseValue,
      unit: "",
      symbol: "",
      formatted: fallbackNumber.value,
      issues
    };
  }

  const { familyConfig, validFamily } = resolved;

  // Unit selection order: overrideUnit > auto-pick by bands.
  const targetUnit = resolveDisplayUnit({
    baseValue: safeBaseValue,
    family: validFamily,
    system: normalizedSystem,
    overrideUnit,
    issues
  });

  // Validate selected unit and recover to base unit if invalid.
  const { unit, meta } = resolveUnitMeta({
    family: validFamily,
    familyConfig,
    unit: targetUnit,
    issues
  });

  // Precision precedence: explicit option > family default.
  const decimals =
    Number.isInteger(precision) && precision >= 0
      ? precision
      : familyConfig.defaultPrecision;

  const symbol = meta.symbol || "";

  const conversion = resolveNumericValue({
    baseValue: safeBaseValue,
    family: validFamily,
    unit
  });
  issues.push(...conversion.issues);

  const numberFmt = formatNumber(conversion.value, normalizedLocale, decimals);
  issues.push(...numberFmt.issues);

  const formatted = symbol
    ? `${numberFmt.value}${NBSP}${symbol}`
    : numberFmt.value;

  return { value: conversion.value, unit, symbol, formatted, issues };
}
